import "./AppSearch.css";
import { Search, User } from "lucide-react";
import { useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { selectUser } from "../../../redux/features/appData";
import { apiGetWorkspace } from "../../../api";
import { apiGetEmployeeRecordsForWorkspace } from "../../../services/EmployeeRecordService";
import { EmployeeRecord } from "winslow-core";
import { erGetCPValue } from "../app-employee-records/employeeRecordUtils";

type AppSearchProps = {
    
}

type SearchResult = {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    fullName: string;
    avatarURL?: string;
}

export const AppSearch = ({} : AppSearchProps) => {
    const navigate = useNavigate();
    const storeUser = useSelector(selectUser);
    
    const [query, setQuery] = useState("");
    const [results, setResults] = useState<SearchResult[]>([]);
    const [isOpen, setIsOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [workspace, setWorkspace] = useState<any>(null);
    const [employeeRecords, setEmployeeRecords] = useState<EmployeeRecord[]>([]);
    
    const searchRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    // Load workspace to check HRIS type
    useEffect(() => {
        if (storeUser?.currentWorkspaceId) {
            (async () => {
                const workspaceData = await apiGetWorkspace(storeUser.currentWorkspaceId);
                setWorkspace(workspaceData);
            })();
        }
    }, [storeUser?.currentWorkspaceId]);

    // Load employee records
    useEffect(() => {
        if (storeUser?.currentWorkspaceId && workspace?.hasNativeHRIS) {
            (async () => {
                const records = await apiGetEmployeeRecordsForWorkspace(storeUser.currentWorkspaceId!);
                setEmployeeRecords(records || []);
            })();
        }
    }, [storeUser?.currentWorkspaceId, workspace?.hasNativeHRIS]);

    // Search logic
    useEffect(() => {
        if (query.length < 2) {
            setResults([]);
            setIsOpen(false);
            return;
        }

        if (!workspace?.hasNativeHRIS) {
            setResults([]);
            setIsOpen(false);
            return;
        }

        setIsLoading(true);
        
        const searchResults = employeeRecords
            .filter((er: EmployeeRecord) => {
                const firstName = erGetCPValue(er, "system-firstName") || "";
                const lastName = erGetCPValue(er, "system-lastName") || "";
                const email = er.workEmail || "";
                
                const searchTerm = query.toLowerCase();
                return (
                    firstName.toLowerCase().includes(searchTerm) ||
                    lastName.toLowerCase().includes(searchTerm) ||
                    email.toLowerCase().includes(searchTerm)
                );
            })
            .slice(0, 5)
            .map((er: EmployeeRecord) => ({
                id: er.id,
                firstName: erGetCPValue(er, "system-firstName") || "",
                lastName: erGetCPValue(er, "system-lastName") || "",
                email: er.workEmail || "",
                fullName: `${erGetCPValue(er, "system-firstName") || ""} ${erGetCPValue(er, "system-lastName") || ""}`.trim(),
                avatarURL: er.avatarURL
            }));

        setResults(searchResults);
        setIsOpen(searchResults.length > 0);
        setIsLoading(false);
    }, [query, employeeRecords, workspace?.hasNativeHRIS]);

    // Handle click outside to close dropdown
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setQuery(e.target.value);
    };

    const handleResultClick = (result: SearchResult) => {
        navigate(`/app/employee-records/view/${result.id}`);
        setQuery("");
        setIsOpen(false);
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Escape') {
            setIsOpen(false);
        }
    };

    return (
        <>
            <div className="app-search" ref={searchRef}>
                <input 
                    ref={inputRef}
                    type="text"
                    placeholder={workspace?.hasNativeHRIS ? "Search employees..." : "Search"}
                    className="app-search-input"
                    value={query}
                    onChange={handleInputChange}
                    onKeyDown={handleKeyDown}
                    disabled={!workspace?.hasNativeHRIS}
                />
                <div className="app-search-icon">
                    <Search size={16} />
                </div>
                
                {isOpen && (
                    <div className="app-search-results">
                        {isLoading ? (
                            <div className="app-search-loading">Searching...</div>
                        ) : results.length > 0 ? (
                            results.map((result) => (
                                <div 
                                    key={result.id}
                                    className="app-search-result"
                                    onClick={() => handleResultClick(result)}
                                >
                                    <div className="app-search-result-avatar">
                                        {result.avatarURL ? (
                                            <img 
                                                src={result.avatarURL} 
                                                alt={result.fullName}
                                                className="app-search-result-avatar-img"
                                            />
                                        ) : (
                                            <div className="app-search-result-avatar-placeholder">
                                                {result.firstName?.charAt(0) || ''}{result.lastName?.charAt(0) || ''}
                                            </div>
                                        )}
                                    </div>
                                    <div className="app-search-result-content">
                                        <div className="app-search-result-name">{result.fullName}</div>
                                        <div className="app-search-result-email">{result.email}</div>
                                    </div>
                                </div>
                            ))
                        ) : (
                            <div className="app-search-no-results">No employees found</div>
                        )}
                    </div>
                )}
            </div>
        </>
    )

}